/**
 * Types and utilities for the guarantee transaction system
 */

export interface GuaranteeTransaction {
	guaranteeTransactionId: string;
	contractCardId: string;
	facilityId: string;
	guaranteeTransactionTypeId: number;
	grossAmount: number;
	netAmount: number;
	vatAmount: number;
	vatPercentage: number;
	createdAt: string;
	notes?: string | null;
	reversesTransactionId?: string | null;
	replacedCardId?: string | null;
	guaranteeTransactionType: {
		guaranteeTransactionTypeId: number;
		name: string;
		involvesCashMovement: boolean;
		isFiscal: boolean;
	};
	processedByUser: {
		userId: string;
		firstName?: string | null;
		lastName?: string | null;
	};
	reversedByTransaction?: {
		guaranteeTransactionId: string;
		createdAt: string;
	} | null;
}

export interface GuaranteeTransactionType {
	guaranteeTransactionTypeId: number;
	name: string;
	involvesCashMovement: boolean;
	isFiscal: boolean;
}

/**
 * Determines if a transaction has been voided (reversed by another transaction).
 */
export const isTransactionVoided = (tx: GuaranteeTransaction): boolean => {
	return tx.reversedByTransaction != null;
};

/**
 * Determines if a transaction is a void/reversal itself.
 */
export const isVoidTransaction = (tx: GuaranteeTransaction): boolean => {
	return tx.reversesTransactionId != null;
};

/**
 * Maps guarantee transaction type enum IDs to their string identifiers.
 */
export const GUARANTEE_TYPE = {
	DEPOSIT: 'DEPOSIT',
	RETURN: 'RETURN',
	REPLACEMENT: 'REPLACEMENT',
	DEPOSIT_TRANSFER: 'DEPOSIT_TRANSFER',
	VOID: 'VOID',
} as const;

/**
 * Format a currency amount for display.
 */
export const formatGuaranteeAmount = (amount: number | null | undefined): string => {
	if (amount == null) return '—';
	return new Intl.NumberFormat('en', {
		style: 'currency',
		currency: 'EUR',
	}).format(amount);
};
